/**
 * NymoArt Shopify Integration - Base Module v2.0.0
 * 
 * Provides:
 * - Event Bus for module communication
 * - Firebase configuration and helpers
 * - Shared utilities
 * - Module registry
 * 
 * @author NymoArt
 * @version 2.0.0
 */

window.NymoArt = window.NymoArt || (function() {
  'use strict';
  
  // ===== CONFIGURATION =====
  const config = {
    version: '2.0.0',
    debug: false,
    firebase: {
      projectId: 'nymo-design',
      get baseUrl() {
        return `https://firestore.googleapis.com/v1/projects/${this.projectId}/databases/(default)/documents`;
      }
    }
  };
  
  // ===== LOGGING =====
  const log = {
    info: (...args) => config.debug && console.log('🎨 [NymoArt]', ...args),
    warn: (...args) => console.warn('🎨 [NymoArt]', ...args),
    error: (...args) => console.error('🎨 [NymoArt]', ...args),
    event: (name, data) => config.debug && console.log(`🎯 [NymoArt Event] ${name}`, data)
  };
  
  // ===== EVENT BUS =====
  const events = {
    _listeners: {},
    
    /**
     * Subscribe to an event
     * @param {string} eventName - Event name (e.g., 'nymo:code:detected')
     * @param {function} callback - Callback function receiving event data
     * @returns {function} Unsubscribe function
     */
    on(eventName, callback) {
      if (!this._listeners[eventName]) {
        this._listeners[eventName] = [];
      }
      this._listeners[eventName].push(callback);
      log.info(`Subscribed to: ${eventName}`);
      
      // Return unsubscribe function
      return () => {
        this._listeners[eventName] = this._listeners[eventName].filter(cb => cb !== callback);
        log.info(`Unsubscribed from: ${eventName}`);
      };
    },
    
    /**
     * Subscribe to an event (one-time)
     * @param {string} eventName - Event name
     * @param {function} callback - Callback function
     */
    once(eventName, callback) {
      const unsubscribe = this.on(eventName, (data) => {
        unsubscribe();
        callback(data);
      });
    },
    
    /**
     * Emit an event
     * @param {string} eventName - Event name
     * @param {object} data - Event data payload
     */
    emit(eventName, data = {}) {
      log.event(eventName, data);
      
      // Call registered listeners
      if (this._listeners[eventName]) {
        this._listeners[eventName].forEach(callback => {
          try {
            callback(data);
          } catch (error) {
            log.error(`Error in event listener for ${eventName}:`, error);
          }
        });
      }
      
      // Also dispatch DOM CustomEvent for legacy/external support
      document.dispatchEvent(new CustomEvent(eventName, { 
        detail: data,
        bubbles: true 
      }));
    },
    
    /**
     * Remove all listeners for an event
     * @param {string} eventName - Event name
     */
    off(eventName) {
      delete this._listeners[eventName];
      log.info(`Removed all listeners for: ${eventName}`);
    }
  };
  
  // ===== FIREBASE HELPERS =====
  const firebase = {
    /**
     * Get full document URL
     * @param {string} collection - Collection name
     * @param {string} docId - Document ID
     * @returns {string} Full Firestore REST API URL
     */
    getDocUrl(collection, docId) {
      return `${config.firebase.baseUrl}/${collection}/${docId}`;
    },
    
    /**
     * Fetch document from Firestore
     * @param {string} collection - Collection name
     * @param {string} docId - Document ID
     * @param {AbortSignal} [signal] - Optional abort signal for cancellation
     * @returns {Promise<object>} Parsed document data
     */
    async getDoc(collection, docId, signal = null) {
      const url = this.getDocUrl(collection, docId);
      log.info(`Fetching: ${collection}/${docId}`);
      
      const options = signal ? { signal } : {};
      const response = await fetch(url, options);
      
      if (!response.ok) {
        throw new Error(`Document not found: ${collection}/${docId} (${response.status})`);
      }
      
      const rawData = await response.json();
      return this.parseDocument(rawData);
    },
    
    /**
     * Parse Firestore document to plain object
     * @param {object} doc - Raw Firestore document
     * @returns {object} Parsed document with simple values
     */
    parseDocument(doc) {
      if (!doc.fields) return {};
      
      const result = {};
      for (const [key, value] of Object.entries(doc.fields)) {
        result[key] = this.parseField(value);
      }
      return result;
    },
    
    /**
     * Parse a single Firestore field value
     * @param {object} field - Firestore field object
     * @returns {any} Parsed value
     */
    parseField(field) {
      if (!field) return null;
      
      if (field.stringValue !== undefined) return field.stringValue;
      if (field.integerValue !== undefined) return parseInt(field.integerValue, 10);
      if (field.doubleValue !== undefined) return parseFloat(field.doubleValue);
      if (field.booleanValue !== undefined) return field.booleanValue;
      if (field.timestampValue !== undefined) return new Date(field.timestampValue);
      if (field.nullValue !== undefined) return null;
      
      if (field.arrayValue !== undefined) {
        return (field.arrayValue.values || []).map(v => this.parseField(v));
      }
      
      if (field.mapValue !== undefined) {
        return this.parseDocument({ fields: field.mapValue.fields });
      }
      
      return null;
    },
    
    /**
     * Update/create document in Firestore (PATCH)
     * @param {string} collection - Collection name
     * @param {string} docId - Document ID
     * @param {object} data - Data to write (will be converted to Firestore format)
     * @returns {Promise<object>} Updated document
     */
    async updateDoc(collection, docId, data) {
      const url = this.getDocUrl(collection, docId);
      log.info(`Updating: ${collection}/${docId}`);
      
      const firestoreData = this.toFirestoreFormat(data);
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields: firestoreData })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update: ${collection}/${docId} (${response.status})`);
      }
      
      return response.json();
    },
    
    /**
     * Convert plain object to Firestore format
     * @param {object} data - Plain object
     * @returns {object} Firestore-formatted fields
     */
    toFirestoreFormat(data) {
      const result = {};
      
      for (const [key, value] of Object.entries(data)) {
        if (value === null || value === undefined) {
          result[key] = { nullValue: null };
        } else if (typeof value === 'string') {
          result[key] = { stringValue: value };
        } else if (typeof value === 'number') {
          if (Number.isInteger(value)) {
            result[key] = { integerValue: value.toString() };
          } else {
            result[key] = { doubleValue: value };
          }
        } else if (typeof value === 'boolean') {
          result[key] = { booleanValue: value };
        } else if (value instanceof Date) {
          result[key] = { timestampValue: value.toISOString() };
        } else if (Array.isArray(value)) {
          result[key] = { 
            arrayValue: { 
              values: value.map(v => this.toFirestoreFormat({ v }).v) 
            } 
          };
        }
      }
      
      return result;
    }
  };
  
  // ===== MODULE REGISTRY =====
  const modules = {
    _registered: {},
    
    /**
     * Register a module
     * @param {string} name - Module name (e.g., 'qr-scanner-abc123')
     * @param {object} instance - Module instance
     */
    register(name, instance) {
      this._registered[name] = instance;
      log.info(`Module registered: ${name}`);
      
      events.emit('nymo:module:ready', {
        moduleName: name,
        instance: instance
      });
    },
    
    /**
     * Unregister a module
     * @param {string} name - Module name
     */
    unregister(name) {
      delete this._registered[name];
      log.info(`Module unregistered: ${name}`);
    },
    
    /**
     * Get a registered module
     * @param {string} name - Module name
     * @returns {object|undefined} Module instance
     */
    get(name) {
      return this._registered[name];
    },
    
    /**
     * Get all registered modules
     * @returns {object} All modules
     */
    getAll() {
      return { ...this._registered };
    }
  };
  
  // ===== UTILITIES =====
  const utils = {
    /**
     * Generate unique ID
     * @param {string} [prefix] - Optional prefix
     * @returns {string} Unique ID
     */
    uniqueId(prefix = '') {
      return `${prefix}${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    },
    
    /**
     * Debounce function
     * @param {function} func - Function to debounce
     * @param {number} wait - Wait time in ms
     * @returns {function} Debounced function
     */
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },
    
    /**
     * Format date for display
     * @param {Date|string} date - Date to format
     * @param {string} [locale] - Locale (default: 'de-DE')
     * @returns {string} Formatted date
     */
    formatDate(date, locale = 'de-DE') {
      const d = date instanceof Date ? date : new Date(date);
      return d.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };
  
  // ===== PUBLIC API =====
  const api = {
    version: config.version,
    config,
    events,
    firebase,
    modules,
    utils,
    log,
    
    /**
     * Enable debug mode
     */
    enableDebug() {
      config.debug = true;
      log.info('Debug mode enabled');
    },
    
    /**
     * Disable debug mode
     */
    disableDebug() {
      config.debug = false;
    }
  };
  
  // Log initialization
  console.log(`🎨 NymoArt Base Module v${config.version} initialized`);
  
  return api;
})();

// ===== EVENT DEFINITIONS (for documentation) =====
/**
 * @event nymo:code:detected
 * Fired when a QR code or manual code is detected
 * @type {CustomEvent}
 * @property {object} detail
 * @property {string} detail.designId - The design code
 * @property {string} detail.source - 'qr' or 'manual'
 * @property {object} detail.imageData - Full image data from Firebase
 */

/**
 * @event nymo:code:reset
 * Fired when the QR scanner is reset
 * @type {CustomEvent}
 */

/**
 * @event nymo:vote:submitted
 * Fired when a vote is submitted
 * @type {CustomEvent}
 * @property {object} detail
 * @property {string} detail.designId
 * @property {string} detail.voteType - 'like' or 'dislike'
 */

/**
 * @event nymo:coffee:loaded
 * Fired when coffee info is loaded
 * @type {CustomEvent}
 * @property {object} detail
 * @property {string} detail.batchId
 * @property {object} detail.batchData
 */

/**
 * @event nymo:module:ready
 * Fired when a module is initialized
 * @type {CustomEvent}
 * @property {object} detail
 * @property {string} detail.moduleName
 */

